/**
 * LERNZENTRUM — Typen des Funktionsregisters
 *
 * Verbindliche Schnittstelle aus `docs/lernzentrum-status.md` („Schnittstellen")
 * und `docs/lernzentrum-tutorial-plan.md` §1.1. Jedes weitere Paket (Tour,
 * Pipeline, Chat, Übungsprojekte) liest NUR diese Formen — sie sind die eine
 * Quelle der Wahrheit. Wer hier etwas umbenennt, bricht alle Pakete.
 *
 * Wichtig: Ein Kapitel beschreibt eine FUNKTION, nicht eine Datei. Die Anker
 * (`data-tutorial="…"`) sind reine Attribute an bestehenden Knöpfen und
 * Feldern — kein Verhalten, keine Fachlogik.
 */

import type { AppView, UserProfile } from '../types';
import type { StartRolle } from '../services/rechte/katalog';

// ─── Rollen ──────────────────────────────────────────────────────────────────

/**
 * Vier Lernrollen (Plan §5). Bewusst NICHT deckungsgleich mit
 * `UserProfile['role']`: dort gibt es fünf Werte, von denen zwei
 * (`arbeitsvorbereitung`, `admin`) denselben Lernstoff sehen.
 *
 *  - `K` Koordinator (`user`, Fachrolle SiGeKo/SiFa/HSE) — draußen, Begehung, SiGe-Plan
 *  - `I` Innendienst (`arbeitsvorbereitung`, `admin`, Büro-Rolle) — Projekte, Rechnungen, CRM
 *  - `V` Verwaltung  (`super_admin`)   — Admin-Konsole
 *  - `F` Freelancer  (`freelancer`)    — nur das Freelancer-Portal
 *
 * Seit dem Rollensystem (Nr. 149/150) zählen für `user` und
 * `arbeitsvorbereitung` auch die fachlichen Rollen — siehe `rollenAusProfil`.
 */
export type TutorialRolle = 'K' | 'I' | 'V' | 'F';

/**
 * Kachel im Lernzentrum. Der Wert ist immer das Präfix der Kapitel-ID vor dem
 * ersten Punkt — `begehung.mangel-foto` liegt im Bereich `begehung`. Die Liste
 * deckt den vollen Kapitelkatalog (Plan §5) ab, auch was v1 noch nicht baut;
 * so muss beim Nachziehen eines Kapitels dieser Typ nicht angefasst werden.
 */
export type TutorialBereich =
    | 'einstieg'
    | 'projekt'
    | 'begehung'
    | 'sigeplan'
    | 'einweisung'
    | 'planung'
    | 'vorank'
    | 'unterlage'
    | 'asplan'
    | 'bauordnung'
    | 'sifa'
    | 'crm'
    | 'angebot'
    | 'freelancer'
    | 'rechnung'
    | 'buchhaltung'
    | 'erp'
    | 'admin';

/**
 * Was in einem Schritt mit dem Anker passiert.
 *
 *  - `klick`  — der Knopf wird gedrückt
 *  - `tippen` — es wird etwas eingegeben (`wert`)
 *  - `zeigen` — nur Spotlight, nichts wird bedient
 *  - `warten` — auf ein Ergebnis warten (Speichern, KI-Antwort, PDF-Aufbau)
 *
 * Die Tour im Browser führt `klick` und `tippen` NIE aus (Plan §1.3) — sie
 * zeigt sie nur. Ausgeführt werden sie allein vom Pipeline-Roboter gegen das
 * Demo-Projekt.
 */
export type TutorialAktion = 'klick' | 'tippen' | 'zeigen' | 'warten';

/** Stabile Kapitel-Kennung, z. B. `begehung.mangel-foto`. Wird nie umbenannt. */
export type TutorialKapitelId = string;

// ─── Kapitel und Schritt ─────────────────────────────────────────────────────

export interface TutorialSchritt {
    /** `data-tutorial`-Wert des Zielelements, Konvention `<bereich>.<element>`. */
    anker: string;
    aktion: TutorialAktion;
    /** Nur bei `tippen`: der Text, den der Roboter eingibt. */
    wert?: string;
    /** Ein Satz, was hier passiert. Rohstoff für Anleitung, Blase und Sprechertext. */
    stichwort: string;
}

export interface TutorialKapitel {
    /** Stabil, nie umbenennen — an dieser ID hängen Fortschritt und Medien. */
    id: TutorialKapitelId;
    bereich: TutorialBereich;
    titel: string;
    /** Wo die Tour startet. */
    view: AppView;
    /** Wer das Kapitel im Lernzentrum sieht. */
    rollen: TutorialRolle[];
    /** Fehlt der Wert, gilt `beide`. */
    appModus?: 'sigeko' | 'hse' | 'beide';
    dauerMin: number;
    schritte: TutorialSchritt[];
    /** Rahmen fürs Praxisbeispiel; die Pipeline formuliert daraus den Fließtext. */
    praxis: { kontext: string; ziel: string };
    /** Kuratiert aus Vorfall-Memos (Paket A5), nie frei erfunden. */
    stolperfallen?: string[];
    voraussetzungen?: TutorialKapitelId[];
}

// ─── Rollenabbildung ─────────────────────────────────────────────────────────

/**
 * Die EINE Rolle, unter der ein Profil in der Pipeline aufgenommen wird
 * (ein Demo-Nutzer je Rolle, Plan §4 Schritt 2).
 *
 * `admin` fällt hier auf `I`, obwohl ein Admin im Lernzentrum auch die
 * Koordinatorkapitel sieht — fürs Aufnehmen braucht es genau einen Nutzer.
 * Was jemand SIEHT, beantwortet `rollenAusProfil`.
 */
export const rolleAusProfil = (role: UserProfile['role']): TutorialRolle => {
    switch (role) {
        case 'user':
            return 'K';
        case 'arbeitsvorbereitung':
        case 'admin':
            return 'I';
        case 'super_admin':
            return 'V';
        case 'freelancer':
            return 'F';
    }
};

/**
 * Fachrollen (Rollensystem, `benutzer_rollen`), die ein internes Profil zum
 * Koordinator draußen machen — Lernrolle `K`.
 */
export const KOORDINATOR_ROLLEN: readonly StartRolle[] = ['sigeko', 'sifa', 'hse'];

/** Büro-Rollen, die ein internes Profil zum Innendienst machen — Lernrolle `I`. */
export const INNENDIENST_ROLLEN: readonly StartRolle[] = ['arbeitsvorbereitung', 'vertrieb', 'disposition', 'buchhaltung'];

/**
 * Startrollen, die an den Lernrollen nichts ändern: Zusatzaufgaben, die
 * jemand neben seiner Arbeit draußen oder im Büro trägt (Beschaffung aus
 * Nr. 179). Sie zählen wie eine eigene Rolle eines Admins. Steht hier, damit
 * jede Startrolle bewusst einer der drei Listen zugeordnet ist.
 */
export const ZUSATZ_ROLLEN: readonly StartRolle[] = ['beschaffung'];

const KOORDINATOR_MENGE: ReadonlySet<string> = new Set(KOORDINATOR_ROLLEN);
const INNENDIENST_MENGE: ReadonlySet<string> = new Set(INNENDIENST_ROLLEN);

/**
 * Nur bei diesen Systemrollen ändern die fachlichen Rollen etwas an den
 * Lernrollen — für alle anderen muss niemand `benutzer_rollen` lesen.
 */
export const lernrollenHaengenAnRollen = (role: UserProfile['role'] | null | undefined): boolean =>
    role === 'user' || role === 'arbeitsvorbereitung';

/**
 * Alle Rollen, deren Kapitel ein Profil im Lernzentrum sehen darf.
 *
 * Absichtlich weiter als `rolleAusProfil`: Ein `admin` arbeitet im Innendienst,
 * muss aber erklären können, was der Koordinator draußen tut — er bekommt `K`
 * und `I`. Ein `super_admin` sieht zusätzlich die Verwaltung. Ein `freelancer`
 * bleibt strikt auf `F`: die Rolle ist die einzige EXTERNE (siehe Kommentar an
 * `UserProfile['role']` in `src/types.ts`) und darf nie mit `user`
 * gleichgesetzt werden — auch nicht in der Hilfe, sonst steht in seinem
 * Lernzentrum eine Anleitung für Bildschirme, die er nie zu sehen bekommt.
 *
 * `fachrollen` sind die Schlüssel aus `benutzer_rollen` (Rollensystem,
 * Nr. 149/150) der heutigen Firma des Profils. Sie zählen nur für `user` und
 * `arbeitsvorbereitung`:
 *  - `K`, wenn eine Fachrolle dabei ist (`KOORDINATOR_ROLLEN`),
 *  - `I`, wenn eine Büro-Rolle dabei ist (`INNENDIENST_ROLLEN`) oder die
 *    Systemrolle `arbeitsvorbereitung` ist,
 *  - keine von beiden: wie vor dem Rollensystem (`user` `K`,
 *    `arbeitsvorbereitung` `I`).
 * Eigene Rollen eines Admins zählen nicht — ihre Schlüssel stehen in keiner
 * der beiden Listen. Fehlt `fachrollen` (Migration offen, Tabelle nicht
 * lesbar, noch nicht geladen), gilt die Systemrolle allein, genau wie früher.
 *
 * ZWEI ORTE, EINE WAHRHEIT: zeichengleich zu `public.tutorial_rollen()`
 * (supabase/tutorial_rollen_fachrollen.sql, Nr. 173) — die Funktion steht in
 * der Lesepolicy von `tutorial_kapitel`. Wer eine der beiden Stellen ändert,
 * ändert beide; `register.test.ts` vergleicht die beiden Listen mit der
 * SQL-Datei.
 */
export const rollenAusProfil = (
    role: UserProfile['role'],
    fachrollen?: readonly string[],
): TutorialRolle[] => {
    const koordinator = !!fachrollen?.some((r) => KOORDINATOR_MENGE.has(r));
    const innendienst = !!fachrollen?.some((r) => INNENDIENST_MENGE.has(r));
    switch (role) {
        case 'user':
            if (koordinator && innendienst) return ['K', 'I'];
            return innendienst ? ['I'] : ['K'];
        case 'arbeitsvorbereitung':
            return koordinator ? ['K', 'I'] : ['I'];
        case 'admin':
            return ['K', 'I'];
        case 'super_admin':
            return ['K', 'I', 'V'];
        case 'freelancer':
            return ['F'];
    }
};
